import React, { useState, useEffect, useContext } from 'react';
import {
    Box,
    Typography,
    Card,
    CardContent,
    Button,
    Grid,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Paper,
    IconButton,
    Chip,
    TextField,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Alert,
    CircularProgress,
    Tabs,
    Tab,
    InputAdornment,
} from '@mui/material';
import { useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { colors } from '../components/Theme/WebTheme';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import DeleteIcon from '@mui/icons-material/Delete';
import AddIcon from '@mui/icons-material/Add';
import PhotoCameraIcon from '@mui/icons-material/PhotoCamera';
import CompareArrowsIcon from '@mui/icons-material/CompareArrows';
import LocalTaxiIcon from '@mui/icons-material/LocalTaxi';
import EvStationIcon from '@mui/icons-material/EvStation';
import AirportShuttleIcon from '@mui/icons-material/AirportShuttle';
import AutoFixHighIcon from '@mui/icons-material/AutoFixHigh';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import SyncIcon from '@mui/icons-material/Sync';
import DirectionsCarIcon from '@mui/icons-material/DirectionsCar';
import ElectricCarIcon from '@mui/icons-material/ElectricCar';
import { FirebaseContext } from 'common';
import { registerNewWebTranslations } from '../locales/newWebTranslations';

// Ride-hailing service colors
const serviceColors = {
    uber: '#000000',
    bolt: '#34D186',
    'bergen taxi': '#f59e0b',
};

// Category icons
const categoryIcons = {
    '1-4 standard': <LocalTaxiIcon sx={{ color: '#6b7280' }} />,
    '5-6 maxi': <AirportShuttleIcon sx={{ color: '#3b82f6' }} />,
    // Legacy mappings for old data
    'miljobil': <EvStationIcon sx={{ color: '#22c55e' }} />,
    'eco': <EvStationIcon sx={{ color: '#22c55e' }} />,
    'eco-car': <EvStationIcon sx={{ color: '#22c55e' }} />,
    'bensin': <LocalTaxiIcon sx={{ color: '#6b7280' }} />,
    'diesel': <LocalTaxiIcon sx={{ color: '#6b7280' }} />,
    'bensin og diesel': <LocalTaxiIcon sx={{ color: '#6b7280' }} />,
    'maxi': <AirportShuttleIcon sx={{ color: '#3b82f6' }} />,
    'maxi taxi': <AirportShuttleIcon sx={{ color: '#3b82f6' }} />,
};

const CompetitorPrices = () => {
    const { t, i18n } = useTranslation();
    const auth = useSelector(state => state.auth);
    const firebase = useContext(FirebaseContext);

    const [priceData, setPriceData] = useState([]);
    const [routes, setRoutes] = useState([]);
    const [companies, setCompanies] = useState([]);
    const [categories, setCategories] = useState([]);
    const [loading, setLoading] = useState(true);
    const [uploadDialogOpen, setUploadDialogOpen] = useState(false);
    const [manualEntryOpen, setManualEntryOpen] = useState(false);
    const [selectedImage, setSelectedImage] = useState(null);
    const [imagePreview, setImagePreview] = useState(null);
    const [uploading, setUploading] = useState(false);
    const [activeTab, setActiveTab] = useState(0);
    const [selectedRoute, setSelectedRoute] = useState('all');
    const [selectedCompany, setSelectedCompany] = useState('all');
    const [selectedCategory, setSelectedCategory] = useState('all');
    const [priceSort, setPriceSort] = useState('none');
    const [fetchingBergenTaxi, setFetchingBergenTaxi] = useState(false);
    const [fetchingUber, setFetchingUber] = useState(false);
    const [fetchingBolt, setFetchingBolt] = useState(false);

    // OCR extraction state
    const [extracting, setExtracting] = useState(false);
    const [extractedPrices, setExtractedPrices] = useState([]);
    const [rawOcrText, setRawOcrText] = useState('');
    const [showExtractedPrices, setShowExtractedPrices] = useState(false);

    useEffect(() => {
        registerNewWebTranslations(i18n);
    }, [i18n]);

    // Manual entry form state
    const [manualEntry, setManualEntry] = useState({
        route_from: '',
        route_to: '',
        distance_km: '',
        company: '',
        category: '1-4 standard',
        price: '',
        vehicle: '',
        vehicle_year: '',
    });

    // Load data from Firebase
    useEffect(() => {
        // Always set a timeout to stop loading - in case Firebase never responds
        const timeout = setTimeout(() => {
            setLoading(false);
        }, 3000);

        if (!firebase || !firebase.database) {
            return () => clearTimeout(timeout);
        }

        const pricesRef = firebase.database.ref('competitor_prices');

        const handleData = (snapshot) => {
            clearTimeout(timeout);
            const data = snapshot.val();
            if (data) {
                const pricesArray = Object.entries(data).map(([id, value]) => ({
                    id,
                    ...value
                }));
                setPriceData(pricesArray);

                // Extract unique routes, companies, and categories
                const uniqueRoutes = [...new Set(pricesArray.map(p => `${p.route_from} → ${p.route_to}`))];
                const uniqueCompanies = [...new Set(pricesArray.map(p => p.company).filter(Boolean))];
                const uniqueCategories = [...new Set(pricesArray.map(p => p.category).filter(Boolean))];
                setRoutes(uniqueRoutes);
                setCompanies(uniqueCompanies);
                setCategories(uniqueCategories);
            } else {
                setPriceData([]);
                setRoutes([]);
                setCompanies([]);
                setCategories([]);
            }
            setLoading(false);
        };

        const handleError = (error) => {
            console.error('Firebase error:', error);
            clearTimeout(timeout);
            setLoading(false);
        };

        pricesRef.on('value', handleData, handleError);

        return () => {
            clearTimeout(timeout);
            pricesRef.off('value', handleData);
        };
    }, [firebase]);

    // Handle image selection
    const handleImageSelect = (event) => {
        const file = event.target.files[0];
        if (file) {
            setSelectedImage(file);
            const reader = new FileReader();
            reader.onloadend = () => {
                setImagePreview(reader.result);
            };
            reader.readAsDataURL(file);
        }
    };

    // Extract prices from image using OCR
    const handleExtractPrices = async () => {
        if (!selectedImage || !imagePreview) return;

        setExtracting(true);
        setExtractedPrices([]);
        setRawOcrText('');

        try {
            // Get base64 data from preview (remove data URL prefix)
            const base64Data = imagePreview.split(',')[1];

            // Call Cloud Function for OCR
            const response = await fetch(
                'https://us-central1-reaplnprge.cloudfunctions.net/googleapis-extract_kiosk_prices',
                {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({ imageBase64: base64Data })
                }
            );

            const result = await response.json();

            if (result.success) {
                setRawOcrText(result.rawText || '');
                setExtractedPrices(result.extractedPrices || []);
                setShowExtractedPrices(true);

                if (result.extractedPrices?.length === 0) {
                    alert(t('extract_none_alert'));
                }
            } else {
                throw new Error(result.error || 'OCR extraction failed');
            }
        } catch (error) {
            console.error('OCR Error:', error);
            alert(t('extract_error', { message: error.message }));
        }
        setExtracting(false);
    };

    // Upload image and store reference
    const handleUploadImage = async () => {
        if (!selectedImage || !firebase) return;

        setUploading(true);
        try {
            const timestamp = Date.now();
            const imageRef = firebase.storage.ref(`competitor_kiosk_images/${timestamp}_${selectedImage.name}`);
            await imageRef.put(selectedImage);
            const downloadURL = await imageRef.getDownloadURL();

            // Store image reference in database
            const imagesRef = firebase.database.ref('competitor_kiosk_images');
            await imagesRef.push({
                url: downloadURL,
                uploaded_at: timestamp,
                uploaded_by: auth.profile?.uid || 'unknown',
                processed: extractedPrices.length > 0,
                notes: ''
            });

            setUploadDialogOpen(false);
            setSelectedImage(null);
            setImagePreview(null);
            setShowExtractedPrices(false);
            setExtractedPrices([]);
            setRawOcrText('');
        } catch (error) {
            console.error('Error uploading image:', error);
            alert(t('upload_image_error', { message: error.message }));
        }
        setUploading(false);
    };

    // Save extracted prices to database
    const handleSaveExtractedPrices = async () => {
        if (!firebase || extractedPrices.length === 0) return;

        setUploading(true);
        try {
            const pricesRef = firebase.database.ref('competitor_prices');
            const timestamp = Date.now();

            for (const entry of extractedPrices) {
                await pricesRef.push({
                    route_from: entry.route_from,
                    route_to: entry.route_to,
                    company: entry.company,
                    category: entry.category,
                    price: entry.price,
                    vehicle: entry.vehicle || '',
                    vehicle_year: entry.vehicle_year || null,
                    distance_km: null,
                    recorded_at: timestamp,
                    recorded_by: auth.profile?.uid || 'unknown',
                    extracted_by_ocr: true
                });
            }

            // Also upload the image
            await handleUploadImage();

            alert(t('save_prices_success', { count: extractedPrices.length }));
        } catch (error) {
            console.error('Error saving prices:', error);
            alert(t('saving_prices_error', { message: error.message }));
        }
        setUploading(false);
    };

    // Update an extracted price entry
    const handleUpdateExtractedPrice = (index, field, value) => {
        const updated = [...extractedPrices];
        updated[index] = { ...updated[index], [field]: value };
        setExtractedPrices(updated);
    };

    // Remove an extracted price entry
    const handleRemoveExtractedPrice = (index) => {
        setExtractedPrices(extractedPrices.filter((_, i) => i !== index));
    };

    // Handle manual entry submission
    const handleManualEntrySubmit = async () => {
        if (!manualEntry.route_from || !manualEntry.route_to || !manualEntry.company || !manualEntry.price) {
            alert(t('missing_fields'));
            return;
        }

        if (!firebase) return;

        try {
            const pricesRef = firebase.database.ref('competitor_prices');
            await pricesRef.push({
                ...manualEntry,
                price: parseFloat(manualEntry.price),
                distance_km: manualEntry.distance_km ? parseFloat(manualEntry.distance_km) : null,
                vehicle_year: manualEntry.vehicle_year ? parseInt(manualEntry.vehicle_year) : null,
                recorded_at: Date.now(),
                recorded_by: auth.profile?.uid || 'unknown',
            });

            setManualEntryOpen(false);
            setManualEntry({
                route_from: '',
                route_to: '',
                distance_km: '',
                company: '',
                category: '1-4 standard',
                price: '',
                vehicle: '',
                vehicle_year: '',
            });
        } catch (error) {
            console.error('Error saving entry:', error);
            alert(t('saving_entry_error', { message: error.message }));
        }
    };

    // Fetch Bergen Taxi prices manually
    const handleFetchBergenTaxi = async () => {
        setFetchingBergenTaxi(true);
        try {
            const response = await fetch(
                'https://us-central1-reaplnprge.cloudfunctions.net/googleapis-fetch_bergen_taxi_price_manual?save=true'
            );
            const result = await response.json();

            if (result.success) {
                alert(t('bergen_taxi_imported', {
                    standard: result.prices['1-4_standard'].weekday,
                    maxi: result.prices['5-6_seat_maxi'].weekday,
                    count: result.entries_saved
                }));
            } else {
                throw new Error(result.error || 'Failed to fetch prices');
            }
        } catch (error) {
            console.error('Error fetching Bergen Taxi prices:', error);
            alert(t('fetch_bergen_error', { message: error.message }));
        }
        setFetchingBergenTaxi(false);
    };

    // Fetch Uber prices (estimated based on Bergen market rates)
    const handleFetchUber = async () => {
        setFetchingUber(true);
        try {
            const response = await fetch(
                'https://us-central1-reaplnprge.cloudfunctions.net/googleapis-fetch_uber_prices?save=true'
            );
            const result = await response.json();

            if (result.success) {
                const breakdown = result.price_breakdown?.uberX;
                const feeInfo = breakdown ? t('uber_fee_info', {
                    base: breakdown.base,
                    airport: breakdown.airport,
                    toll: breakdown.toll
                }) : '';
                alert(t('uber_prices_imported', {
                    uberX: result.prices.uberX,
                    comfort: result.prices.uberComfort,
                    xl: result.prices.uberXL,
                    feeInfo,
                    count: result.entries_saved
                }));
            } else {
                throw new Error(result.error || 'Failed to fetch prices');
            }
        } catch (error) {
            console.error('Error fetching Uber prices:', error);
            alert(t('fetch_uber_error', { message: error.message }));
        }
        setFetchingUber(false);
    };

    // Fetch Bolt prices (estimated based on Bergen market rates)
    const handleFetchBolt = async () => {
        setFetchingBolt(true);
        try {
            const response = await fetch(
                'https://us-central1-reaplnprge.cloudfunctions.net/googleapis-fetch_bolt_prices?save=true'
            );
            const result = await response.json();

            if (result.success) {
                const breakdown = result.price_breakdown?.bolt;
                const feeInfo = breakdown ? t('bolt_fee_info', {
                    base: breakdown.base,
                    airport: breakdown.airport,
                    toll: breakdown.toll
                }) : '';
                alert(t('bolt_prices_imported', {
                    bolt: result.prices.bolt,
                    comfort: result.prices.boltComfort,
                    xl: result.prices.boltXL,
                    feeInfo,
                    count: result.entries_saved
                }));
            } else {
                throw new Error(result.error || 'Failed to fetch prices');
            }
        } catch (error) {
            console.error('Error fetching Bolt prices:', error);
            alert(t('fetch_bolt_error', { message: error.message }));
        }
        setFetchingBolt(false);
    };

    // Delete price entry
    const handleDeleteEntry = async (id) => {
        if (window.confirm(t('delete_entry_confirm'))) {
            if (!firebase) return;
            try {
                await firebase.database.ref(`competitor_prices/${id}`).remove();
            } catch (error) {
                console.error('Error deleting entry:', error);
                alert(t('delete_entry_error', { message: error.message }));
            }
        }
    };

    // Get filtered data
    const getFilteredData = () => {
        let filtered = priceData;

        // Filter by route
        if (selectedRoute !== 'all') {
            filtered = filtered.filter(p => `${p.route_from} → ${p.route_to}` === selectedRoute);
        }

        // Filter by company (sentral)
        if (selectedCompany !== 'all') {
            filtered = filtered.filter(p => p.company === selectedCompany);
        }

        // Filter by category (car type)
        if (selectedCategory !== 'all') {
            filtered = filtered.filter(p => p.category === selectedCategory);
        }

        // Sort by price
        if (priceSort === 'low') {
            filtered = [...filtered].sort((a, b) => a.price - b.price);
        } else if (priceSort === 'high') {
            filtered = [...filtered].sort((a, b) => b.price - a.price);
        }

        return filtered;
    };

    // Get price statistics
    const getStats = () => {
        const filtered = getFilteredData();
        if (filtered.length === 0) return null;

        const prices = filtered.map(p => p.price);
        const ourPrices = filtered.filter(p => p.company?.toLowerCase().includes('plukk'));

        return {
            min: Math.min(...prices),
            max: Math.max(...prices),
            avg: Math.round(prices.reduce((a, b) => a + b, 0) / prices.length),
            count: filtered.length,
            ourLowest: ourPrices.length > 0 ? Math.min(...ourPrices.map(p => p.price)) : null,
        };
    };

    // Group data by category
    const getGroupedByCategory = () => {
        const filtered = getFilteredData();
        const grouped = {};

        filtered.forEach(entry => {
            const cat = entry.category?.toLowerCase() || 'other';
            if (!grouped[cat]) grouped[cat] = [];
            grouped[cat].push(entry);
        });

        // Sort each category by price
        Object.keys(grouped).forEach(cat => {
            grouped[cat].sort((a, b) => a.price - b.price);
        });

        return grouped;
    };

    // Compare with our prices
    const getPriceComparison = () => {
        const filtered = getFilteredData();
        const comparisons = {};

        companies.forEach(company => {
            const companyPrices = filtered.filter(p => p.company === company);
            if (companyPrices.length > 0) {
                const avgPrice = companyPrices.reduce((a, b) => a + b.price, 0) / companyPrices.length;
                comparisons[company] = {
                    avgPrice: Math.round(avgPrice),
                    count: companyPrices.length,
                    categories: [...new Set(companyPrices.map(p => p.category))]
                };
            }
        });

        return comparisons;
    };

    const stats = getStats();
    const groupedData = getGroupedByCategory();
    const comparison = getPriceComparison();

    if (loading) {
        return (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '50vh' }}>
                <CircularProgress />
            </Box>
        );
    }

    return (
        <Box sx={{ p: 3 }}>
            {/* Header */}
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
                <Box>
                    <Typography variant="h4" sx={{ fontWeight: 700, color: colors.DARK || '#111827' }}>
                        {t('competitor_prices') || 'Competitor Prices'}
                    </Typography>
                    <Typography variant="body2" sx={{ color: '#6b7280', mt: 0.5 }}>
                        {t('competitor_prices_subtitle') || 'Track and compare taxi prices from airport kiosks'}
                    </Typography>
                </Box>
                <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                    <Button
                        variant="outlined"
                        startIcon={fetchingUber ? <CircularProgress size={16} /> : <DirectionsCarIcon />}
                        onClick={handleFetchUber}
                        disabled={fetchingUber}
                        sx={{ borderColor: '#000', color: '#000', '&:hover': { borderColor: '#333', bgcolor: 'rgba(0, 0, 0, 0.05)' } }}
                    >
                        {fetchingUber ? t('fetching_uber') : 'Uber'}
                    </Button>
                    <Button
                        variant="outlined"
                        startIcon={fetchingBolt ? <CircularProgress size={16} /> : <ElectricCarIcon />}
                        onClick={handleFetchBolt}
                        disabled={fetchingBolt}
                        sx={{ borderColor: '#34D186', color: '#34D186', '&:hover': { borderColor: '#2ab974', bgcolor: 'rgba(52, 209, 134, 0.05)' } }}
                    >
                        {fetchingBolt ? t('fetching_bolt') : 'Bolt'}
                    </Button>
                    <Button
                        variant="outlined"
                        startIcon={fetchingBergenTaxi ? <CircularProgress size={16} /> : <SyncIcon />}
                        onClick={handleFetchBergenTaxi}
                        disabled={fetchingBergenTaxi}
                        sx={{ borderColor: '#f59e0b', color: '#f59e0b', '&:hover': { borderColor: '#d97706', bgcolor: 'rgba(245, 158, 11, 0.05)' } }}
                    >
                        {fetchingBergenTaxi ? t('fetching_bergen_taxi') : 'Bergen Taxi'}
                    </Button>
                    <Button
                        variant="outlined"
                        startIcon={<PhotoCameraIcon />}
                        onClick={() => setUploadDialogOpen(true)}
                    >
                        {t('upload_kiosk_photo') || 'Upload Photo'}
                    </Button>
                    <Button
                        variant="contained"
                        startIcon={<AddIcon />}
                        onClick={() => setManualEntryOpen(true)}
                        sx={{ bgcolor: colors.PRIMARY || '#2563EB' }}
                    >
                        {t('add_price_entry') || 'Add Entry'}
                    </Button>
                </Box>
            </Box>

            {/* Stats Cards */}
            {stats && (
                <Grid container spacing={2} sx={{ mb: 3 }}>
                    <Grid item xs={12} sm={6} md={3}>
                        <Card sx={{ borderRadius: 2 }}>
                            <CardContent>
                                <Typography variant="body2" color="textSecondary">
                                    {t('lowest_price') || 'Lowest Price'}
                                </Typography>
                                <Typography variant="h4" sx={{ fontWeight: 700, color: '#22c55e' }}>
                                    {stats.min} kr
                                </Typography>
                            </CardContent>
                        </Card>
                    </Grid>
                    <Grid item xs={12} sm={6} md={3}>
                        <Card sx={{ borderRadius: 2 }}>
                            <CardContent>
                                <Typography variant="body2" color="textSecondary">
                                    {t('highest_price') || 'Highest Price'}
                                </Typography>
                                <Typography variant="h4" sx={{ fontWeight: 700, color: '#ef4444' }}>
                                    {stats.max} kr
                                </Typography>
                            </CardContent>
                        </Card>
                    </Grid>
                    <Grid item xs={12} sm={6} md={3}>
                        <Card sx={{ borderRadius: 2 }}>
                            <CardContent>
                                <Typography variant="body2" color="textSecondary">
                                    {t('average_price') || 'Average Price'}
                                </Typography>
                                <Typography variant="h4" sx={{ fontWeight: 700 }}>
                                    {stats.avg} kr
                                </Typography>
                            </CardContent>
                        </Card>
                    </Grid>
                    <Grid item xs={12} sm={6} md={3}>
                        <Card sx={{ borderRadius: 2 }}>
                            <CardContent>
                                <Typography variant="body2" color="textSecondary">
                                    {t('total_entries') || 'Total Entries'}
                                </Typography>
                                <Typography variant="h4" sx={{ fontWeight: 700 }}>
                                    {stats.count}
                                </Typography>
                            </CardContent>
                        </Card>
                    </Grid>
                </Grid>
            )}

            {/* Filters */}
            <Box sx={{ mb: 3, display: 'flex', gap: 2, flexWrap: 'wrap', alignItems: 'center' }}>
                <TextField
                    select
                    label={t('filter_by_route') || 'Route'}
                    value={selectedRoute}
                    onChange={(e) => setSelectedRoute(e.target.value)}
                    SelectProps={{ native: true }}
                    size="small"
                    sx={{ minWidth: 250 }}
                >
                    <option value="all">{t('all_routes') || 'All Routes'}</option>
                    {routes.map(route => (
                        <option key={route} value={route}>{route}</option>
                    ))}
                </TextField>

                <TextField
                    select
                    label={t('sentral') || 'Sentral'}
                    value={selectedCompany}
                    onChange={(e) => setSelectedCompany(e.target.value)}
                    SelectProps={{ native: true }}
                    size="small"
                    sx={{ minWidth: 180 }}
                >
                    <option value="all">{t('all_sentrals') || 'All Sentrals'}</option>
                    {companies.map(company => (
                        <option key={company} value={company}>{company}</option>
                    ))}
                </TextField>

                <TextField
                    select
                    label={t('car_type') || 'Car Type'}
                    value={selectedCategory}
                    onChange={(e) => setSelectedCategory(e.target.value)}
                    SelectProps={{ native: true }}
                    size="small"
                    sx={{ minWidth: 150 }}
                >
                    <option value="all">{t('all_types') || 'All Types'}</option>
                    {categories.map(cat => (
                        <option key={cat} value={cat}>{cat}</option>
                    ))}
                </TextField>

                <TextField
                    select
                    label={t('sort_by_price') || 'Sort by Price'}
                    value={priceSort}
                    onChange={(e) => setPriceSort(e.target.value)}
                    SelectProps={{ native: true }}
                    size="small"
                    sx={{ minWidth: 140 }}
                >
                    <option value="none">{t('default') || 'Default'}</option>
                    <option value="low">{t('price_low_high') || 'Low → High'}</option>
                    <option value="high">{t('price_high_low') || 'High → Low'}</option>
                </TextField>

                {/* Clear filters button */}
                {(selectedRoute !== 'all' || selectedCompany !== 'all' || selectedCategory !== 'all' || priceSort !== 'none') && (
                    <Button
                        variant="outlined"
                        size="small"
                        onClick={() => {
                            setSelectedRoute('all');
                            setSelectedCompany('all');
                            setSelectedCategory('all');
                            setPriceSort('none');
                        }}
                        sx={{ height: 40 }}
                    >
                        {t('clear_filters') || 'Clear Filters'}
                    </Button>
                )}
            </Box>

            {/* Tabs */}
            <Tabs value={activeTab} onChange={(e, v) => setActiveTab(v)} sx={{ mb: 3 }}>
                <Tab label={t('price_recommendations') || 'Price Recommendations'} sx={{ fontWeight: 600 }} />
                <Tab label={t('by_category') || 'By Category'} />
                <Tab label={t('by_company') || 'By Company'} />
                <Tab label={t('all_entries') || 'All Entries'} />
            </Tabs>

            {/* Tab Content */}

            {/* Price Recommendations Tab */}
            {activeTab === 0 && (
                <Box>
                    <Alert severity="info" sx={{ mb: 3 }}>
                        {t('price_rec_info') || 'Compare Rydio Taxi prices with competitors. Recommendations show prices 1-5 kr lower than the cheapest competitor to win customers on FastTravel kiosks.'}
                    </Alert>

                    {/* Price Comparison Table */}
                    <TableContainer component={Paper} sx={{ borderRadius: 2 }}>
                        <Table>
                            <TableHead>
                                <TableRow sx={{ bgcolor: '#f3f4f6' }}>
                                    <TableCell sx={{ fontWeight: 600 }}>{t('route') || 'Route'}</TableCell>
                                    <TableCell sx={{ fontWeight: 600 }}>{t('category') || 'Category'}</TableCell>
                                    <TableCell align="right" sx={{ fontWeight: 600, color: '#ef4444' }}>
                                        {t('competitor_lowest') || 'Competitor Lowest'}
                                    </TableCell>
                                    <TableCell align="right" sx={{ fontWeight: 600, color: '#3b82f6' }}>
                                        {t('rydio_current') || 'Rydio Current'}
                                    </TableCell>
                                    <TableCell align="right" sx={{ fontWeight: 600, color: '#22c55e' }}>
                                        {t('recommended_price') || 'Recommended'}
                                    </TableCell>
                                    <TableCell align="center" sx={{ fontWeight: 600 }}>{t('status') || 'Status'}</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {(() => {
                                    // Group data by route + category
                                    const routeCategories = {};
                                    priceData.forEach(entry => {
                                        const key = `${entry.route_from}→${entry.route_to}|${entry.category}`;
                                        if (!routeCategories[key]) {
                                            routeCategories[key] = {
                                                route_from: entry.route_from,
                                                route_to: entry.route_to,
                                                category: entry.category,
                                                rydio: [],
                                                competitors: []
                                            };
                                        }
                                        // Check if it's Rydio Taxi (case insensitive)
                                        const isRydio = entry.company?.toLowerCase().includes('rydio') ||
                                                       entry.company?.toLowerCase().includes('plukk');
                                        if (isRydio) {
                                            routeCategories[key].rydio.push(entry);
                                        } else {
                                            routeCategories[key].competitors.push(entry);
                                        }
                                    });

                                    return Object.values(routeCategories).map((data, idx) => {
                                        const compLowest = data.competitors.length > 0
                                            ? Math.min(...data.competitors.map(c => c.price))
                                            : null;
                                        const rydioLowest = data.rydio.length > 0
                                            ? Math.min(...data.rydio.map(r => r.price))
                                            : null;
                                        // Recommend 5 kr below competitor to undercut
                                        const recommended = compLowest ? compLowest - 5 : null;

                                        const isWinning = rydioLowest && compLowest && rydioLowest < compLowest;
                                        const isLosing = rydioLowest && compLowest && rydioLowest > compLowest;
                                        const needsAction = !rydioLowest || isLosing;

                                        return (
                                            <TableRow key={idx} sx={{
                                                bgcolor: needsAction ? 'rgba(239, 68, 68, 0.05)' :
                                                         isWinning ? 'rgba(34, 197, 94, 0.05)' : 'inherit'
                                            }}>
                                                <TableCell>
                                                    <Typography variant="body2" sx={{ fontWeight: 500 }}>
                                                        {data.route_from} → {data.route_to}
                                                    </Typography>
                                                </TableCell>
                                                <TableCell>
                                                    <Chip
                                                        icon={categoryIcons[data.category?.toLowerCase()]}
                                                        label={data.category}
                                                        size="small"
                                                        sx={{ textTransform: 'capitalize' }}
                                                    />
                                                </TableCell>
                                                <TableCell align="right">
                                                    {compLowest ? (
                                                        <Typography variant="body2" sx={{ fontWeight: 600, color: '#ef4444' }}>
                                                            {compLowest} kr
                                                        </Typography>
                                                    ) : (
                                                        <Typography variant="caption" color="textSecondary">{t('no_data')}</Typography>
                                                    )}
                                                </TableCell>
                                                <TableCell align="right">
                                                    {rydioLowest ? (
                                                        <Typography variant="body2" sx={{
                                                            fontWeight: 600,
                                                            color: isWinning ? '#22c55e' : isLosing ? '#ef4444' : '#3b82f6'
                                                        }}>
                                                            {rydioLowest} kr
                                                        </Typography>
                                                    ) : (
                                                        <Typography variant="caption" color="textSecondary">{t('not_set')}</Typography>
                                                    )}
                                                </TableCell>
                                                <TableCell align="right">
                                                    {recommended ? (
                                                        <Typography variant="body2" sx={{ fontWeight: 700, color: '#22c55e' }}>
                                                            {recommended} kr
                                                        </Typography>
                                                    ) : (
                                                        <Typography variant="caption" color="textSecondary">-</Typography>
                                                    )}
                                                </TableCell>
                                                <TableCell align="center">
                                                    {isWinning ? (
                                                        <Chip label={t('price_winning')} size="small" color="success" />
                                                    ) : isLosing ? (
                                                        <Chip label={t('price_losing')} size="small" color="error" />
                                                    ) : !rydioLowest ? (
                                                        <Chip label={t('price_not_listed')} size="small" color="warning" />
                                                    ) : (
                                                        <Chip label={t('price_equal')} size="small" />
                                                    )}
                                                </TableCell>
                                            </TableRow>
                                        );
                                    });
                                })()}
                            </TableBody>
                        </Table>
                    </TableContainer>

                    {priceData.length === 0 && (
                        <Box sx={{ textAlign: 'center', py: 4 }}>
                            <Typography color="textSecondary">
                                {t('add_competitor_data') || 'Add competitor price data to see recommendations'}
                            </Typography>
                        </Box>
                    )}
                </Box>
            )}

            {/* By Category Tab */}
            {activeTab === 1 && (
                <Grid container spacing={3}>
                    {Object.entries(groupedData).map(([category, entries]) => (
                        <Grid item xs={12} md={6} lg={4} key={category}>
                            <Card sx={{ borderRadius: 2, height: '100%' }}>
                                <CardContent>
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                                        {categoryIcons[category] || <LocalTaxiIcon />}
                                        <Typography variant="h6" sx={{ fontWeight: 600, textTransform: 'capitalize' }}>
                                            {category}
                                        </Typography>
                                        <Chip label={t('entries_with_count', { count: entries.length })} size="small" />
                                    </Box>
                                    <TableContainer>
                                        <Table size="small">
                                            <TableHead>
                                                <TableRow>
                                                    <TableCell>{t('company') || 'Company'}</TableCell>
                                                    <TableCell align="right">{t('price') || 'Price'}</TableCell>
                                                    <TableCell>{t('vehicle') || 'Vehicle'}</TableCell>
                                                </TableRow>
                                            </TableHead>
                                            <TableBody>
                                                {entries.slice(0, 10).map((entry, idx) => (
                                                    <TableRow key={entry.id} sx={{
                                                        bgcolor: idx === 0 ? 'rgba(34, 197, 94, 0.1)' : 'inherit'
                                                    }}>
                                                        <TableCell>
                                                            <Typography variant="body2" sx={{ fontWeight: idx === 0 ? 600 : 400 }}>
                                                                {entry.company}
                                                            </Typography>
                                                        </TableCell>
                                                        <TableCell align="right">
                                                            <Typography
                                                                variant="body2"
                                                                sx={{
                                                                    fontWeight: 600,
                                                                    color: idx === 0 ? '#22c55e' : 'inherit'
                                                                }}
                                                            >
                                                                {entry.price} kr
                                                            </Typography>
                                                        </TableCell>
                                                        <TableCell>
                                                            <Typography variant="caption" color="textSecondary">
                                                                {entry.vehicle} {entry.vehicle_year && `/ ${entry.vehicle_year}`}
                                                            </Typography>
                                                        </TableCell>
                                                    </TableRow>
                                                ))}
                                            </TableBody>
                                        </Table>
                                    </TableContainer>
                                </CardContent>
                            </Card>
                        </Grid>
                    ))}
                </Grid>
            )}

            {/* By Company Tab */}
            {activeTab === 2 && (
                <Grid container spacing={3}>
                    {Object.entries(comparison).sort((a, b) => a[1].avgPrice - b[1].avgPrice).map(([company, data]) => (
                        <Grid item xs={12} sm={6} md={4} lg={3} key={company}>
                            <Card sx={{ borderRadius: 2 }}>
                                <CardContent>
                                    <Typography variant="h6" sx={{ fontWeight: 600, mb: 1 }}>
                                        {company}
                                    </Typography>
                                    <Typography variant="h4" sx={{ fontWeight: 700, color: colors.PRIMARY || '#2563EB' }}>
                                        {data.avgPrice} kr
                                    </Typography>
                                    <Typography variant="caption" color="textSecondary">
                                        {t('avg_price') || 'Avg price'} ({data.count} {t('entries') || 'entries'})
                                    </Typography>
                                    <Box sx={{ mt: 1, display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                                        {data.categories.map(cat => (
                                            <Chip
                                                key={cat}
                                                label={cat}
                                                size="small"
                                                variant="outlined"
                                                sx={{ textTransform: 'capitalize' }}
                                            />
                                        ))}
                                    </Box>
                                </CardContent>
                            </Card>
                        </Grid>
                    ))}
                </Grid>
            )}

            {/* All Entries Tab */}
            {activeTab === 3 && (
                <TableContainer component={Paper} sx={{ borderRadius: 2 }}>
                    <Table>
                        <TableHead>
                            <TableRow>
                                <TableCell>{t('route') || 'Route'}</TableCell>
                                <TableCell>{t('company') || 'Company'}</TableCell>
                                <TableCell>{t('category') || 'Category'}</TableCell>
                                <TableCell align="right">{t('price') || 'Price'}</TableCell>
                                <TableCell>{t('vehicle') || 'Vehicle'}</TableCell>
                                <TableCell>{t('recorded') || 'Recorded'}</TableCell>
                                <TableCell align="right">{t('actions') || 'Actions'}</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {getFilteredData().sort((a, b) => b.recorded_at - a.recorded_at).map((entry) => (
                                <TableRow key={entry.id}>
                                    <TableCell>
                                        <Typography variant="body2">
                                            {entry.route_from} → {entry.route_to}
                                        </Typography>
                                        {entry.distance_km && (
                                            <Typography variant="caption" color="textSecondary">
                                                {entry.distance_km} km
                                            </Typography>
                                        )}
                                    </TableCell>
                                    <TableCell>{entry.company}</TableCell>
                                    <TableCell>
                                        <Chip
                                            icon={categoryIcons[entry.category?.toLowerCase()]}
                                            label={entry.category}
                                            size="small"
                                            sx={{ textTransform: 'capitalize' }}
                                        />
                                    </TableCell>
                                    <TableCell align="right">
                                        <Typography variant="body2" sx={{ fontWeight: 600 }}>
                                            {entry.price} kr
                                        </Typography>
                                    </TableCell>
                                    <TableCell>
                                        <Typography variant="body2">
                                            {entry.vehicle}
                                        </Typography>
                                        {entry.vehicle_year && (
                                            <Typography variant="caption" color="textSecondary">
                                                {entry.vehicle_year}
                                            </Typography>
                                        )}
                                    </TableCell>
                                    <TableCell>
                                        <Typography variant="caption" color="textSecondary">
                                            {entry.recorded_at ? new Date(entry.recorded_at).toLocaleDateString() : '-'}
                                        </Typography>
                                    </TableCell>
                                    <TableCell align="right">
                                        <IconButton
                                            size="small"
                                            onClick={() => handleDeleteEntry(entry.id)}
                                            color="error"
                                        >
                                            <DeleteIcon fontSize="small" />
                                        </IconButton>
                                    </TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                </TableContainer>
            )}

            {/* Empty State */}
            {priceData.length === 0 && (
                <Box sx={{ textAlign: 'center', py: 8 }}>
                    <CompareArrowsIcon sx={{ fontSize: 64, color: '#d1d5db', mb: 2 }} />
                    <Typography variant="h6" color="textSecondary">
                        {t('no_price_data') || 'No price data yet'}
                    </Typography>
                    <Typography variant="body2" color="textSecondary" sx={{ mb: 3 }}>
                        {t('no_price_data_subtitle') || 'Upload kiosk photos or add entries manually to start tracking competitor prices'}
                    </Typography>
                    <Box sx={{ display: 'flex', gap: 2, justifyContent: 'center' }}>
                        <Button
                            variant="outlined"
                            startIcon={<PhotoCameraIcon />}
                            onClick={() => setUploadDialogOpen(true)}
                        >
                            {t('upload_kiosk_photo') || 'Upload Photo'}
                        </Button>
                        <Button
                            variant="contained"
                            startIcon={<AddIcon />}
                            onClick={() => setManualEntryOpen(true)}
                        >
                            {t('add_price_entry') || 'Add Entry'}
                        </Button>
                    </Box>
                </Box>
            )}

            {/* Upload Dialog with OCR */}
            <Dialog
                open={uploadDialogOpen}
                onClose={() => {
                    setUploadDialogOpen(false);
                    setShowExtractedPrices(false);
                    setExtractedPrices([]);
                    setRawOcrText('');
                    setSelectedImage(null);
                    setImagePreview(null);
                }}
                maxWidth={showExtractedPrices ? "lg" : "sm"}
                fullWidth
            >
                <DialogTitle>
                    {showExtractedPrices
                        ? (t('review_extracted_prices') || 'Review Extracted Prices')
                        : (t('upload_kiosk_photo') || 'Upload Kiosk Photo')
                    }
                </DialogTitle>
                <DialogContent>
                    {!showExtractedPrices ? (
                        <>
                            <Alert severity="info" sx={{ mb: 2 }}>
                                {t('upload_kiosk_info_ocr') || 'Upload a photo of the airport kiosk screen. We\'ll automatically extract prices using AI, then you can review and edit before saving.'}
                            </Alert>

                            <Box
                                sx={{
                                    border: '2px dashed #d1d5db',
                                    borderRadius: 2,
                                    p: 4,
                                    textAlign: 'center',
                                    cursor: 'pointer',
                                    '&:hover': { borderColor: colors.PRIMARY || '#2563EB' }
                                }}
                                onClick={() => document.getElementById('kiosk-image-input').click()}
                            >
                                {imagePreview ? (
                                    <Box>
                                        <img
                                            src={imagePreview}
                                            alt="Preview"
                                            style={{ maxWidth: '100%', maxHeight: 300, borderRadius: 8 }}
                                        />
                                    </Box>
                                ) : (
                                    <Box>
                                        <CloudUploadIcon sx={{ fontSize: 48, color: '#9ca3af', mb: 1 }} />
                                        <Typography>{t('click_to_upload') || 'Click to upload image'}</Typography>
                                    </Box>
                                )}
                            </Box>
                            <input
                                id="kiosk-image-input"
                                type="file"
                                accept="image/*"
                                hidden
                                onChange={handleImageSelect}
                            />
                        </>
                    ) : (
                        <>
                            <Grid container spacing={2}>
                                {/* Image preview on the left */}
                                <Grid item xs={12} md={4}>
                                    <Box sx={{ position: 'sticky', top: 0 }}>
                                        {imagePreview && (
                                            <img
                                                src={imagePreview}
                                                alt="Kiosk"
                                                style={{ width: '100%', borderRadius: 8, border: '1px solid #e5e7eb' }}
                                            />
                                        )}
                                        {rawOcrText && (
                                            <Box sx={{ mt: 2 }}>
                                                <Typography variant="caption" color="textSecondary">
                                                    {t('raw_ocr_text') || 'Raw OCR Text:'}
                                                </Typography>
                                                <Paper sx={{ p: 1, maxHeight: 150, overflow: 'auto', bgcolor: '#f9fafb', fontSize: 11 }}>
                                                    <pre style={{ margin: 0, whiteSpace: 'pre-wrap', fontFamily: 'monospace' }}>
                                                        {rawOcrText}
                                                    </pre>
                                                </Paper>
                                            </Box>
                                        )}
                                    </Box>
                                </Grid>

                                {/* Extracted prices on the right */}
                                <Grid item xs={12} md={8}>
                                    {extractedPrices.length === 0 ? (
                                        <Alert severity="warning">
                                            {t('no_prices_extracted') || 'No prices could be automatically extracted. Try a clearer image or add entries manually.'}
                                        </Alert>
                                    ) : (
                                        <>
                                            <Alert severity="success" sx={{ mb: 2 }}>
                                                {t('prices_extracted', { count: extractedPrices.length }) || `Found ${extractedPrices.length} price entries. Review and edit below, then save.`}
                                            </Alert>

                                            {extractedPrices.map((entry, index) => (
                                                <Card key={index} sx={{ mb: 2, p: 2, border: '1px solid #e5e7eb' }}>
                                                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                                                        <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                                                            {t('entry_label', { count: index + 1 })}
                                                        </Typography>
                                                        <Box>
                                                            <Chip
                                                                label={t('confidence_label', { value: entry.confidence || 0 })}
                                                                size="small"
                                                                color={entry.confidence >= 70 ? 'success' : entry.confidence >= 40 ? 'warning' : 'error'}
                                                                sx={{ mr: 1 }}
                                                            />
                                                            <IconButton
                                                                size="small"
                                                                color="error"
                                                                onClick={() => handleRemoveExtractedPrice(index)}
                                                            >
                                                                <DeleteIcon fontSize="small" />
                                                            </IconButton>
                                                        </Box>
                                                    </Box>

                                                    <Grid container spacing={1}>
                                                        <Grid item xs={6}>
                                                            <TextField
                                                                fullWidth
                                                                size="small"
                                                                label={t('company') || 'Company'}
                                                                value={entry.company}
                                                                onChange={(e) => handleUpdateExtractedPrice(index, 'company', e.target.value)}
                                                            />
                                                        </Grid>
                                                        <Grid item xs={3}>
                                                            <TextField
                                                                fullWidth
                                                                size="small"
                                                                label={t('price') || 'Price'}
                                                                type="number"
                                                                value={entry.price}
                                                                onChange={(e) => handleUpdateExtractedPrice(index, 'price', parseInt(e.target.value))}
                                                                InputProps={{
                                                                    endAdornment: <InputAdornment position="end">kr</InputAdornment>,
                                                                }}
                                                            />
                                                        </Grid>
                                                        <Grid item xs={3}>
                                                            <TextField
                                                                fullWidth
                                                                size="small"
                                                                select
                                                                label={t('category') || 'Category'}
                                                                value={entry.category}
                                                                onChange={(e) => handleUpdateExtractedPrice(index, 'category', e.target.value)}
                                                                SelectProps={{ native: true }}
                                                            >
                                                                <option value="1-4 standard">1-4 Standard</option>
                                                                <option value="5-6 maxi">5-6 Maxi</option>
                                                            </TextField>
                                                        </Grid>
                                                        <Grid item xs={6}>
                                                            <TextField
                                                                fullWidth
                                                                size="small"
                                                                label={t('route_from') || 'From'}
                                                                value={entry.route_from}
                                                                onChange={(e) => handleUpdateExtractedPrice(index, 'route_from', e.target.value)}
                                                            />
                                                        </Grid>
                                                        <Grid item xs={6}>
                                                            <TextField
                                                                fullWidth
                                                                size="small"
                                                                label={t('route_to') || 'To'}
                                                                value={entry.route_to}
                                                                onChange={(e) => handleUpdateExtractedPrice(index, 'route_to', e.target.value)}
                                                            />
                                                        </Grid>
                                                    </Grid>
                                                </Card>
                                            ))}
                                        </>
                                    )}

                                    <Button
                                        variant="outlined"
                                        startIcon={<AddIcon />}
                                        onClick={() => {
                                            setExtractedPrices([
                                                ...extractedPrices,
                                                {
                                                    company: '',
                                                    price: '',
                                                    category: '1-4 standard',
                                                    route_from: 'Bergen Lufthavn',
                                                    route_to: 'Bergen sentrum',
                                                    vehicle: '',
                                                    confidence: 0
                                                }
                                            ]);
                                        }}
                                        sx={{ mt: 1 }}
                                    >
                                        {t('add_manual_entry') || 'Add Entry Manually'}
                                    </Button>
                                </Grid>
                            </Grid>
                        </>
                    )}
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => {
                        setUploadDialogOpen(false);
                        setShowExtractedPrices(false);
                        setExtractedPrices([]);
                        setRawOcrText('');
                        setSelectedImage(null);
                        setImagePreview(null);
                    }}>
                        {t('cancel') || 'Cancel'}
                    </Button>

                    {!showExtractedPrices ? (
                        <>
                            <Button
                                variant="outlined"
                                onClick={handleUploadImage}
                                disabled={!selectedImage || uploading}
                            >
                                {t('upload_only') || 'Upload Only'}
                            </Button>
                            <Button
                                variant="contained"
                                startIcon={extracting ? <CircularProgress size={16} color="inherit" /> : <AutoFixHighIcon />}
                                onClick={handleExtractPrices}
                                disabled={!selectedImage || extracting}
                                sx={{ bgcolor: '#22c55e', '&:hover': { bgcolor: '#16a34a' } }}
                            >
                                {extracting
                                    ? (t('extracting') || 'Extracting...')
                                    : (t('extract_prices') || 'Extract Prices (AI)')
                                }
                            </Button>
                        </>
                    ) : (
                        <>
                            <Button
                                variant="outlined"
                                onClick={() => setShowExtractedPrices(false)}
                            >
                                {t('back') || 'Back'}
                            </Button>
                            <Button
                                variant="contained"
                                startIcon={uploading ? <CircularProgress size={16} color="inherit" /> : <CheckCircleIcon />}
                                onClick={handleSaveExtractedPrices}
                                disabled={uploading || extractedPrices.length === 0}
                                sx={{ bgcolor: '#22c55e', '&:hover': { bgcolor: '#16a34a' } }}
                            >
                                {uploading
                                    ? (t('saving') || 'Saving...')
                                    : (t('save_all_prices', { count: extractedPrices.length }) || `Save ${extractedPrices.length} Prices`)
                                }
                            </Button>
                        </>
                    )}
                </DialogActions>
            </Dialog>

            {/* Manual Entry Dialog */}
            <Dialog open={manualEntryOpen} onClose={() => setManualEntryOpen(false)} maxWidth="sm" fullWidth>
                <DialogTitle>{t('add_price_entry') || 'Add Price Entry'}</DialogTitle>
                <DialogContent>
                    <Grid container spacing={2} sx={{ mt: 1 }}>
                        <Grid item xs={6}>
                            <TextField
                                fullWidth
                                label={t('route_from') || 'From'}
                                value={manualEntry.route_from}
                                onChange={(e) => setManualEntry({ ...manualEntry, route_from: e.target.value })}
                                placeholder="Bergen Lufthavn Terminal"
                                required
                            />
                        </Grid>
                        <Grid item xs={6}>
                            <TextField
                                fullWidth
                                label={t('route_to') || 'To'}
                                value={manualEntry.route_to}
                                onChange={(e) => setManualEntry({ ...manualEntry, route_to: e.target.value })}
                                placeholder="Bergen sentrum"
                                required
                            />
                        </Grid>
                        <Grid item xs={6}>
                            <TextField
                                fullWidth
                                label={t('distance') || 'Distance (km)'}
                                type="number"
                                value={manualEntry.distance_km}
                                onChange={(e) => setManualEntry({ ...manualEntry, distance_km: e.target.value })}
                                placeholder="21"
                            />
                        </Grid>
                        <Grid item xs={6}>
                            <TextField
                                fullWidth
                                label={t('company') || 'Company'}
                                value={manualEntry.company}
                                onChange={(e) => setManualEntry({ ...manualEntry, company: e.target.value })}
                                placeholder="Christiania Taxi"
                                required
                            />
                        </Grid>
                        <Grid item xs={6}>
                            <TextField
                                fullWidth
                                select
                                label={t('category') || 'Category'}
                                value={manualEntry.category}
                                onChange={(e) => setManualEntry({ ...manualEntry, category: e.target.value })}
                                SelectProps={{ native: true }}
                            >
                                <option value="1-4 standard">1-4 Standard</option>
                                <option value="5-6 maxi">5-6 Maxi</option>
                            </TextField>
                        </Grid>
                        <Grid item xs={6}>
                            <TextField
                                fullWidth
                                label={t('price') || 'Price (kr)'}
                                type="number"
                                value={manualEntry.price}
                                onChange={(e) => setManualEntry({ ...manualEntry, price: e.target.value })}
                                placeholder="550"
                                required
                                InputProps={{
                                    endAdornment: <InputAdornment position="end">kr</InputAdornment>,
                                }}
                            />
                        </Grid>
                        <Grid item xs={8}>
                            <TextField
                                fullWidth
                                label={t('vehicle') || 'Vehicle'}
                                value={manualEntry.vehicle}
                                onChange={(e) => setManualEntry({ ...manualEntry, vehicle: e.target.value })}
                                placeholder="Tesla Model Y"
                            />
                        </Grid>
                        <Grid item xs={4}>
                            <TextField
                                fullWidth
                                label={t('year') || 'Year'}
                                type="number"
                                value={manualEntry.vehicle_year}
                                onChange={(e) => setManualEntry({ ...manualEntry, vehicle_year: e.target.value })}
                                placeholder="2024"
                            />
                        </Grid>
                    </Grid>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setManualEntryOpen(false)}>{t('cancel') || 'Cancel'}</Button>
                    <Button variant="contained" onClick={handleManualEntrySubmit}>
                        {t('save') || 'Save'}
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
};

export default CompetitorPrices;
